import React, { useState, useEffect, useRef } from 'react';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { api } from '../lib/api';
import { useTranslation } from '../i18nContext';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';

const ENV_TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

// Reusable public contact form. Used both on the standalone ContactPage and as
// the embeddable `contact_form` CMS block so both render EXACTLY the same form
// (subject dropdown from admin config, default preselect, captcha, routing).
export default function ContactForm({ requestedInterpreter = '', requestedName = '' }) {
  const t = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const [subjects, setSubjects] = useState([]);
  const [status, setStatus] = useState('idle');
  const [startedAt] = useState(() => Date.now());
  const [captchaToken, setCaptchaToken] = useState('');
  const [siteKey, setSiteKey] = useState(ENV_TURNSTILE_SITE_KEY);
  const [captchaErrored, setCaptchaErrored] = useState(false);
  const turnstileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get('/public-config')
      .then(({ data }) => {
        if (!alive) return;
        if (data?.turnstile_site_key) setSiteKey(data.turnstile_site_key);
        if (Array.isArray(data?.contact_subjects)) {
          setSubjects(data.contact_subjects);
          const def = data.contact_subjects.find((s) => s.is_default);
          if (def && !requestedInterpreter) {
            const lbl = (t.locale === 'en' ? def.label_en : def.label_es) || def.label_es || def.label_en;
            setForm((prev) => (prev.subject ? prev : { ...prev, subject: lbl }));
          }
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill when arriving from "Solicitar este intérprete" CTA in profile page.
  useEffect(() => {
    if (!requestedInterpreter || !requestedName) return;
    const isEs = t.locale === 'es';
    setForm((prev) => ({
      ...prev,
      subject: isEs
        ? `Solicitud de servicios — ${requestedName}`
        : `Service request — ${requestedName}`,
      message: prev.message || (isEs
        ? `Hola, me gustaría contactar a ${requestedName} para un proyecto de interpretación.\n\n• Fechas tentativas:\n• Idiomas requeridos:\n• Ubicación / modalidad (presencial / remoto):\n• Tipo de evento (conferencia, jurídico, médico, etc.):\n• Duración estimada:\n\nGracias.`
        : `Hi, I'd like to contact ${requestedName} for an interpretation project.\n\n• Tentative dates:\n• Required languages:\n• Location / mode (on-site / remote):\n• Event type (conference, legal, medical, etc.):\n• Estimated duration:\n\nThank you.`),
    }));
  }, [requestedInterpreter, requestedName, t.locale]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (subjects.length > 0 && !requestedInterpreter && !form.subject) {
      setStatus('subject_missing');
      setTimeout(() => setStatus('idle'), 6000);
      return;
    }
    if (siteKey && !captchaToken) {
      setStatus('captcha_missing');
      setTimeout(() => setStatus('idle'), 6000);
      return;
    }
    setStatus('sending');
    try {
      await api.post('/contact', {
        ...form,
        started_at: startedAt,
        captcha_token: captchaToken || undefined,
        ...(requestedInterpreter ? { interpreter_slug: requestedInterpreter } : {}),
      });
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '', website: '' });
      setCaptchaToken('');
      if (turnstileRef.current) turnstileRef.current.reset();
    } catch (err) {
      if (err?.response?.status === 429) setStatus('rate_limited');
      else setStatus('error');
      setCaptchaToken('');
      if (turnstileRef.current) turnstileRef.current.reset();
    }
    setTimeout(() => setStatus('idle'), 6000);
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8 border border-gray-100" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="cname" className="block text-sm font-medium text-charcoal-700 mb-2">{t.contact.form.name}</label>
          <input id="cname" type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="contact-name-input"
            className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 text-charcoal-700" />
        </div>
        <div>
          <label htmlFor="cemail" className="block text-sm font-medium text-charcoal-700 mb-2">{t.contact.form.email}</label>
          <input id="cemail" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="contact-email-input"
            className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 text-charcoal-700" />
        </div>
      </div>
      <div className="mb-4">
        <label htmlFor="csubject" className="block text-sm font-medium text-charcoal-700 mb-2">{t.contact.form.subject}</label>
        {subjects.length > 0 && !requestedInterpreter ? (
          <Select value={form.subject || undefined} onValueChange={(v) => setForm({ ...form, subject: v })}>
            <SelectTrigger
              id="csubject"
              data-testid="contact-subject-select"
              className="w-full h-auto px-4 py-3 rounded-lg border border-gray-200 bg-white focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 text-charcoal-700"
            >
              <SelectValue placeholder={t.locale === 'en' ? 'Select a topic…' : 'Selecciona un tema…'} />
            </SelectTrigger>
            <SelectContent>
              {subjects.map((s) => {
                const lbl = (t.locale === 'en' ? s.label_en : s.label_es) || s.label_es || s.label_en;
                return <SelectItem key={s.id || lbl} value={lbl} data-testid={`contact-subject-option-${(s.id || lbl)}`}>{lbl}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        ) : (
          <input id="csubject" type="text" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            data-testid="contact-subject-input"
            className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 text-charcoal-700" />
        )}
      </div>
      <div className="mb-6">
        <label htmlFor="cmsg" className="block text-sm font-medium text-charcoal-700 mb-2">{t.contact.form.message}</label>
        <textarea id="cmsg" rows={12} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="contact-message-input"
          className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 text-charcoal-700 resize-y min-h-[18rem] leading-relaxed" />
      </div>

      {/* Honeypot — invisible to real users; bots scraping inputs tend to fill it. */}
      <div aria-hidden="true" className="hidden" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <label htmlFor="website">Website (no completar)</label>
        <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
      </div>

      {status === 'success' && (
        <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg mb-4" data-testid="contact-success">
          <CheckCircle2 size={18} /><span className="text-sm font-medium">{t.contact.form.success}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg mb-4" data-testid="contact-error">
          <AlertCircle size={18} /><span className="text-sm font-medium">{t.contact.form.error}</span>
        </div>
      )}
      {status === 'rate_limited' && (
        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg mb-4" data-testid="contact-rate-limited">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">
            {t.locale === 'en'
              ? 'You have sent several messages recently. Please wait a few minutes before sending another.'
              : 'Has enviado varios mensajes recientemente. Por favor espera unos minutos antes de enviar otro.'}
          </span>
        </div>
      )}
      {status === 'subject_missing' && (
        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg mb-4" data-testid="contact-subject-missing">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">
            {t.locale === 'en' ? 'Please select a topic before sending.' : 'Por favor selecciona un tema antes de enviar.'}
          </span>
        </div>
      )}
      {status === 'captcha_missing' && (
        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg mb-4" data-testid="contact-captcha-missing">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">
            {t.locale === 'en'
              ? 'Please complete the "I am not a robot" check before sending.'
              : 'Por favor completa la verificación "No soy un robot" antes de enviar.'}
          </span>
        </div>
      )}

      {siteKey && (
        <div className="mb-4" data-testid="contact-captcha">
          <Turnstile
            ref={turnstileRef}
            siteKey={siteKey}
            options={{ theme: 'light', language: t.locale === 'en' ? 'en' : 'es' }}
            onSuccess={(token) => { setCaptchaToken(token); setCaptchaErrored(false); }}
            onError={() => { setCaptchaToken(''); setCaptchaErrored(true); }}
            onExpire={() => setCaptchaToken('')}
          />
          {captchaErrored && (
            <p className="text-xs text-amber-700 mt-1.5 flex items-center gap-1" data-testid="contact-captcha-error">
              <AlertCircle size={12} />
              {t.locale === 'en'
                ? 'The verification could not load. If you are an administrator, make sure this domain is allowed in the Cloudflare Turnstile widget.'
                : 'La verificación no se pudo cargar. Si eres administrador, agrega este dominio a la lista permitida del widget de Cloudflare Turnstile.'}
            </p>
          )}
        </div>
      )}

      <button type="submit" disabled={status === 'sending'} data-testid="contact-submit-btn"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cmic-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-cmic-600 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed">
        {status === 'sending' ? (
          <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t.contact.form.sending}</>
        ) : (
          <><Send size={18} />{t.contact.form.send}</>
        )}
      </button>
    </form>
  );
}
